import React, { useState, useEffect, useMemo } from 'react';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedFooter from '../common/UnifiedFooter';
import EcommerceAuthModal from '../ecommerce/EcommerceAuthModal';
import EcommerceCartDrawer from '../ecommerce/EcommerceCartDrawer';
import EcommerceWhatsAppFloating from '../ecommerce/EcommerceWhatsAppFloating';
import { useWebStore } from '../../context/WebStoreContext';
import Swal from 'sweetalert2';

const DEFAULT_DOWNLOAD_ITEMS = [
  {
    id: 'down-1',
    title: 'Lista de Precios Mayorista - Vigente',
    description: 'Precios actualizados de fábrica para compras por bulto cerrado y distribuidores mayoristas de todo el país.',
    category: 'Listas de Precios',
    fileUrl: '',
    fileFormat: 'PDF',
    fileSize: '1.2 MB',
    updatedAt: 'Marzo 2026',
    requiresAuth: false,
    is_active: true,
    order: 1
  },
  {
    id: 'down-2',
    title: 'Catálogo Institucional Completo y Gama de Productos',
    description: 'Folleto general con especificaciones, medidas, gramajes y fotografías de todos los artículos.',
    category: 'Catálogos PDF',
    fileUrl: '',
    fileFormat: 'PDF',
    fileSize: '4.8 MB',
    updatedAt: '2026',
    requiresAuth: false,
    is_active: true,
    order: 2
  },
  {
    id: 'down-3',
    title: 'Ficha Técnica y Medidas de Cajas de Cartón',
    description: 'Planos de armado, resistencia al aplastamiento y tolerancias estándar para embalaje y logística.',
    category: 'Planos y Fichas Técnicas',
    fileUrl: '',
    fileFormat: 'PDF',
    fileSize: '850 KB',
    updatedAt: '2026',
    requiresAuth: false,
    is_active: true,
    order: 3
  },
  {
    id: 'down-4',
    title: 'Planilla de Pedidos Rápida (Formato Excel)',
    description: 'Plantilla estructurada para confeccionar pedidos masivos por código de artículo y enviarla por WhatsApp o correo.',
    category: 'Listas de Precios',
    fileUrl: '',
    fileFormat: 'XLSX',
    fileSize: '320 KB',
    updatedAt: '2026',
    requiresAuth: false,
    is_active: true,
    order: 4
  }
];

const getFormatBadge = (format = '') => {
  const f = (format || '').toUpperCase().trim();
  switch (f) {
    case 'PDF':
      return { icon: 'bi-file-earmark-pdf-fill', bg: 'bg-danger-subtle text-danger border-danger-subtle', label: 'PDF' };
    case 'XLS':
    case 'XLSX':
    case 'CSV':
      return { icon: 'bi-file-earmark-excel-fill', bg: 'bg-success-subtle text-success border-success-subtle', label: f || 'EXCEL' };
    case 'DOC':
    case 'DOCX':
      return { icon: 'bi-file-earmark-word-fill', bg: 'bg-primary-subtle text-primary border-primary-subtle', label: f || 'WORD' };
    case 'ZIP':
    case 'RAR':
      return { icon: 'bi-file-earmark-zip-fill', bg: 'bg-warning-subtle text-warning-emphasis border-warning-subtle', label: f || 'ZIP' };
    case 'PNG':
    case 'JPG':
    case 'JPEG':
    case 'WEBP':
      return { icon: 'bi-file-earmark-image-fill', bg: 'bg-info-subtle text-info-emphasis border-info-subtle', label: f || 'IMAGEN' };
    default:
      return { icon: 'bi-file-earmark-arrow-down-fill', bg: 'bg-secondary-subtle text-secondary border-secondary-subtle', label: f || 'ARCHIVO' };
  }
};

const DownloadsPage = ({ user, onLogout }) => {
  const { landingConfig, storeConfig, rubros, ecomCustomer: contextCustomer, setEcomCustomer: setContextCustomer, logoutCustomer } = useWebStore();

  const [ecomCustomer, setEcomCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_customer');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Mantener ecomCustomer sincronizado con el contexto global
  useEffect(() => {
    setEcomCustomer(contextCustomer || null);
  }, [contextCustomer]);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('TODOS');

  const downloadsConfig = landingConfig?.downloadsSection || {};
  const isSectionEnabled = downloadsConfig.enabled !== false;
  const isAuthRequired = downloadsConfig.visibility === 'authenticated';
  const isUserAuthenticated = Boolean(user || ecomCustomer);

  const rawItems = (downloadsConfig.items && Array.isArray(downloadsConfig.items) && downloadsConfig.items.length > 0)
    ? downloadsConfig.items
    : DEFAULT_DOWNLOAD_ITEMS;

  const activeItems = useMemo(() => {
    return rawItems
      .filter(item => item.is_active !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [rawItems]);

  const categories = useMemo(() => {
    const set = new Set();
    activeItems.forEach(item => {
      if (item.category && item.category.trim()) {
        set.add(item.category.trim());
      }
    });
    return ['TODOS', ...Array.from(set)];
  }, [activeItems]);

  const filteredItems = useMemo(() => {
    return activeItems.filter(item => {
      const matchCat = selectedCategory === 'TODOS' || (item.category && item.category.trim() === selectedCategory);
      const term = searchTerm.toLowerCase().trim();
      const matchText = !term ||
        (item.title && item.title.toLowerCase().includes(term)) ||
        (item.description && item.description.toLowerCase().includes(term)) ||
        (item.category && item.category.toLowerCase().includes(term));

      // Filtrado estricto por lista de precios asignada al cliente
      if (ecomCustomer?.n_lista && item.targetPriceList) {
        if (Number(item.targetPriceList) !== Number(ecomCustomer.n_lista)) {
          return false;
        }
      }

      return matchCat && matchText;
    });
  }, [activeItems, selectedCategory, searchTerm, ecomCustomer]);

  const handleDownload = (item) => {
    if ((isAuthRequired || item.requiresAuth) && !isUserAuthenticated) {
      Swal.fire({
        icon: 'info',
        title: 'Acceso Exclusivo',
        text: 'Por favor, iniciá sesión o registrate para descargar este archivo.',
        confirmButtonText: 'Iniciar Sesión',
        confirmButtonColor: '#0d6efd',
        showCancelButton: true,
        cancelButtonText: 'Cancelar'
      }).then((res) => {
        if (res.isConfirmed) {
          setAuthModalMode('login');
          setIsAuthModalOpen(true);
        }
      });
      return;
    }

    if (ecomCustomer?.n_lista && item.targetPriceList && Number(item.targetPriceList) !== Number(ecomCustomer.n_lista)) {
      Swal.fire({
        icon: 'warning',
        title: 'Lista de Precios Diferencial',
        text: `Tu cuenta tiene habilitada la Lista ${ecomCustomer.n_lista}. Para solicitar acceso a otra lista o escala, contactá a un asesor comercial.`,
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    if (item.fileUrl && item.fileUrl.trim()) {
      const link = document.createElement('a');
      link.href = item.fileUrl;
      link.target = '_blank';
      link.download = item.filename || item.title || 'descarga';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      Swal.fire({
        icon: 'success',
        title: 'Descarga en Proceso',
        text: `Se está preparando la descarga de "${item.title}". Si el archivo no se abre automáticamente, contactate con nuestro equipo.`,
        confirmButtonColor: '#0d6efd',
        timer: 3000
      });
    }
  };

  const handleAuthSuccess = (customerData) => {
    setEcomCustomer(customerData);
    if (setContextCustomer) {
      setContextCustomer(customerData);
    } else {
      localStorage.setItem('sigecom_ecom_customer', JSON.stringify(customerData));
      localStorage.setItem('sigecom_ecom_last_activity', Date.now().toString());
    }
    setIsAuthModalOpen(false);
    Swal.fire({
      icon: 'success',
      title: '¡Sesión Iniciada!',
      text: `Bienvenido/a, ${customerData.d_nombre || customerData.d_razon || 'Cliente'}. Ya tenés acceso a todas las descargas.`,
      timer: 2500,
      confirmButtonColor: '#0d6efd'
    });
  };

  const companyName = storeConfig?.logoText || landingConfig?.footer?.companyName || 'Nuestra Empresa';

  return (
    <div className="webstore-site-wrapper d-flex flex-column min-vh-100" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', backgroundColor: 'var(--ecom-bg, #f8fafc)', color: 'var(--ecom-text, #1e293b)' }}>
      
      {/* 1. Header Unificado */}
      <UnifiedHeader
        currentUser={ecomCustomer || user}
        cartCount={0}
        activePage="descargas"
        onOpenCart={() => setIsCartOpen(true)}
        onLogout={() => {
          if (logoutCustomer) {
            logoutCustomer(true);
          } else {
            localStorage.removeItem('sigecom_ecom_customer');
            localStorage.removeItem('sigecom_ecom_last_activity');
            setEcomCustomer(null);
          }
        }}
        onAuthClick={(mode) => {
          setAuthModalMode(mode || 'login');
          setIsAuthModalOpen(true);
        }}
        priceListInfo="Lista de Precios"
        canSeePrices={true}
        topBarConfig={landingConfig?.topBar}
        storeConfig={storeConfig}
        rubros={rubros}
      />

      {/* 2. Banner Superior Institucional (Mismo estilo que Novedades, Empresa, Contacto) */}
      <div 
        className="text-white py-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, var(--ecom-primary, #0d6efd) 0%, var(--ecom-secondary, #0a58ca) 100%)'
        }}
      >
        <div className="container">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
            <div>
              <span className="badge bg-warning text-dark fw-bold px-3 py-1 rounded-pill text-uppercase mb-2">
                Listas & Recursos Comerciales
              </span>
              <h1 className="fw-black fs-2 mb-0">
                {downloadsConfig.title || 'Centro de Descargas y Listas de Precios'}
              </h1>
            </div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><a href="/" className="text-white opacity-75 text-decoration-none">Inicio</a></li>
                <li className="breadcrumb-item active text-white fw-bold" aria-current="page">Descargas</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* 3. Contenido Principal */}
      <main className="flex-grow-1 py-5">
        <div className="container">

          {/* CASO: Sección deshabilitada en configuración */}
          {!isSectionEnabled ? (
            <div className="card border-0 shadow-sm rounded-4 p-5 text-center bg-white my-4">
              <div className="display-4 text-muted mb-3">
                <i className="bi bi-slash-circle"></i>
              </div>
              <h4 className="fw-bold text-dark mb-2">Sección No Disponible</h4>
              <p className="text-muted mb-4">El centro de descargas se encuentra temporalmente deshabilitado por la administración.</p>
              <div>
                <a href="/" className="btn btn-primary rounded-pill px-4 py-2 fw-semibold">
                  Volver al Inicio
                </a>
              </div>
            </div>
          ) : isAuthRequired && !isUserAuthenticated ? (
            /* CASO: Sección con Visibilidad Exclusiva para Usuarios Autenticados */
            <div className="row justify-content-center my-4">
              <div className="col-12 col-md-8 col-lg-6">
                <div className="card border-0 shadow-sm rounded-4 p-4 p-md-5 text-center bg-white">
                  <div 
                    className="rounded-circle bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center mx-auto mb-3 shadow-xs"
                    style={{ width: '72px', height: '72px', fontSize: '2rem' }}
                  >
                    <i className="bi bi-shield-lock-fill"></i>
                  </div>
                  <h4 className="fw-bold text-dark mb-2">Área Exclusiva para Clientes</h4>
                  <p className="text-muted mb-4">
                    Para acceder y descargar las listas de precios vigentes, planos técnicos y material comercial de <strong>{companyName}</strong>, por favor ingresá con tu cuenta registrada.
                  </p>
                  <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">
                    <button
                      type="button"
                      className="btn btn-primary rounded-pill px-4 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                      onClick={() => {
                        setAuthModalMode('login');
                        setIsAuthModalOpen(true);
                      }}
                    >
                      <i className="bi bi-box-arrow-in-right"></i>
                      <span>Iniciar Sesión</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-pill px-4 py-2.5 fw-semibold"
                      onClick={() => {
                        setAuthModalMode('register');
                        setIsAuthModalOpen(true);
                      }}
                    >
                      Solicitar Registro
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* CASO: Sección Habilitada con Lista de Archivos */
            <>
              {/* Barra de Búsqueda y Filtros de Categoría */}
              <div className="card border-0 shadow-sm rounded-4 bg-white p-3 p-md-4 mb-4">
                <div className="row g-3 align-items-center justify-content-between">
                  {/* Buscador */}
                  <div className="col-12 col-md-5">
                    <div className="input-group">
                      <span className="input-group-text bg-light border-end-0 rounded-start-pill ps-3">
                        <i className="bi bi-search text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control bg-light border-start-0 rounded-end-pill shadow-none"
                        placeholder="Buscar por título o descripción..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          className="btn btn-light border-0 position-absolute end-0 top-50 translate-middle-y me-2 rounded-circle p-1"
                          style={{ zIndex: 5 }}
                          onClick={() => setSearchTerm('')}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Pills de Categorías */}
                  <div className="col-12 col-md-7">
                    <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                      {categories.map((cat, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`btn btn-sm rounded-pill px-3 py-1.5 fw-semibold transition-all ${selectedCategory === cat ? 'btn-primary shadow-xs' : 'btn-outline-secondary'}`}
                          onClick={() => setSelectedCategory(cat)}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Grilla de Descargas */}
              {filteredItems.length === 0 ? (
                <div className="card border-0 shadow-sm rounded-4 p-5 text-center bg-white my-4">
                  <div className="display-4 text-muted mb-3">
                    <i className="bi bi-folder-x"></i>
                  </div>
                  <h5 className="fw-bold text-dark mb-1">No se encontraron archivos</h5>
                  <p className="text-muted small mb-3">Probá con otro término de búsqueda o seleccioná otra categoría.</p>
                  <div>
                    <button
                      type="button"
                      className="btn btn-outline-primary rounded-pill px-3 py-1.5 fw-semibold btn-sm"
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedCategory('TODOS');
                      }}
                    >
                      Limpiar Filtros
                    </button>
                  </div>
                </div>
              ) : (
                <div className="row g-4">
                  {filteredItems.map((item, idx) => {
                    const badgeInfo = getFormatBadge(item.fileFormat);
                    return (
                      <div key={item.id || idx} className="col-12 col-md-6 col-xl-4">
                        <div className="card border-0 shadow-sm rounded-4 h-100 bg-white p-4 d-flex flex-column justify-content-between hover-lift transition-all">
                          <div>
                            {/* Cabecera de la Card */}
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                              <div 
                                className="rounded-4 p-3 d-flex align-items-center justify-content-center shadow-xs"
                                style={{ width: '56px', height: '56px', backgroundColor: '#f8fafc' }}
                              >
                                <i className={`bi ${badgeInfo.icon} fs-2`}></i>
                              </div>
                              <div className="d-flex flex-column align-items-end gap-1">
                                <span className={`badge border rounded-pill px-2.5 py-1 fw-bold ${badgeInfo.bg}`} style={{ fontSize: '0.72rem' }}>
                                  {badgeInfo.label}
                                </span>
                                {item.category && (
                                  <span className="badge bg-light text-muted border rounded-pill px-2 py-0.5" style={{ fontSize: '0.68rem' }}>
                                    {item.category}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Título y Descripción */}
                            <h5 className="fw-bold text-dark mb-2" style={{ fontSize: '1.05rem', lineHeight: '1.3' }}>
                              {item.title}
                            </h5>
                            <p className="text-muted small mb-3" style={{ fontSize: '0.84rem', lineHeight: '1.45', minHeight: '40px' }}>
                              {item.description || 'Archivo disponible para descarga directa.'}
                            </p>
                          </div>

                          <div>
                            {/* Metadatos (Peso, Fecha, Autenticación) */}
                            <div className="d-flex align-items-center justify-content-between py-2 mb-3 border-top border-bottom small text-muted" style={{ fontSize: '0.78rem' }}>
                              <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-hdd text-secondary"></i>
                                <span>{item.fileSize || 'Descarga rápida'}</span>
                              </div>
                              {item.updatedAt && (
                                <div className="d-flex align-items-center gap-2">
                                  <i className="bi bi-calendar3 text-secondary"></i>
                                  <span>{item.updatedAt}</span>
                                </div>
                              )}
                              {item.requiresAuth && (
                                <span className="badge bg-warning-subtle text-warning-emphasis rounded-pill d-inline-flex align-items-center gap-1" title="Requiere iniciar sesión">
                                  <i className="bi bi-lock-fill"></i> Clientes
                                </span>
                              )}
                            </div>

                            {/* Botón de Descarga */}
                            <button
                              type="button"
                              className="btn btn-primary w-100 rounded-pill py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
                              onClick={() => handleDownload(item)}
                            >
                              <i className="bi bi-download"></i>
                              <span>Descargar {badgeInfo.label}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Banner de Ayuda / Asistencia Directa */}
          <div className="card border-0 shadow-sm rounded-4 bg-primary text-white p-4 p-md-5 mt-5">
            <div className="row align-items-center g-3">
              <div className="col-12 col-md-8 text-center text-md-start">
                <h4 className="fw-bold mb-1">¿Buscás una cotización o material específico a medida?</h4>
                <p className="mb-0 text-white-50 small">
                  Escribinos directamente a nuestro equipo de ventas para recibir listas personalizadas, presupuestos por volumen o fichas técnicas de producción.
                </p>
              </div>
              <div className="col-12 col-md-4 text-center text-md-end">
                <a
                  href={`https://wa.me/${landingConfig?.topBar?.whatsapp || '5491142476622'}?text=${encodeURIComponent('¡Hola! Me comunico desde la sección de Descargas para solicitar información personalizada.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-light rounded-pill px-4 py-2.5 fw-bold text-primary shadow-sm d-inline-flex align-items-center gap-2"
                >
                  <i className="bi bi-whatsapp text-success fs-5"></i>
                  <span>Consultar por WhatsApp</span>
                </a>
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* 4. Footer Unificado */}
      <UnifiedFooter 
        landingConfig={landingConfig}
        storeConfig={storeConfig}
      />

      {/* 5. Modales Auxiliares */}
      <EcommerceAuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialMode={authModalMode}
        onLoginSuccess={handleAuthSuccess}
      />

      <EcommerceCartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={[]}
        onUpdateQuantity={() => {}}
        onRemoveItem={() => {}}
        onProceedToCheckout={() => {}}
        currencySymbol="$"
      />

      {landingConfig?.whatsappFloating?.enabled !== false && (
        <EcommerceWhatsAppFloating
          phone={landingConfig?.whatsappFloating?.phone || '5491142476622'}
          message={landingConfig?.whatsappFloating?.message || `¡Hola! Me comunico desde la sección de Descargas de ${companyName}.`}
          pillText={landingConfig?.whatsappFloating?.pillText || '¿Dudas? ¡Escribinos!'}
          showPill={landingConfig?.whatsappFloating?.showPill !== false}
        />
      )}

    </div>
  );
};

export default DownloadsPage;
